import { lazy } from 'react';
import { App as AntApp, ConfigProvider } from 'antd';
import enGB from 'antd/locale/en_GB';
import arEG from 'antd/locale/ar_EG';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { antdThemeFor } from '../theme/applyTheme';
import { useTheme } from './theme';
import { screenRoutes } from './routes';
import { extraRoutes } from './routes.extra';
import { RequireAuth } from './RequireAuth';
import { PermissionGate } from './PermissionGate';
import { AppShell } from '../components/AppShell';
import Home from './Home';
import { useUi } from './ui';
import { queryClient } from './queryClient';

const Login = lazy(() => import('../screens/Login'));
const NotFound = lazy(() => import('../screens/NotFound'));

// /login → RequireAuth (token + /auth/me) → AppShell → PermissionGate (403 page when the route's permission is missing) → screens.
const router = createBrowserRouter([
  { path: '/login', Component: Login },
  {
    Component: RequireAuth,
    children: [
      {
        Component: AppShell,
        children: [
          { index: true, Component: Home },
          { Component: PermissionGate, children: [...screenRoutes, ...extraRoutes] },
          { path: '*', Component: NotFound },
        ],
      },
    ],
  },
]);

// ConfigProvider carries theme + direction (RTL when Arabic); AntApp provides message/modal/notification with the theme applied.
export function App() {
  const lang = useUi((s) => s.lang);
  // Ant Design is configured from the live theme, so its components move with the CSS variables instead of staying on
  // the colours that were compiled in
  const theme = useTheme((s) => s.theme);
  return (
    <ConfigProvider theme={antdThemeFor(theme)} direction={lang === 'ar' ? 'rtl' : 'ltr'} locale={lang === 'ar' ? arEG : enGB}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}
